// src/components/preferences/CompensationPreferenceEditor.tsx
/**
 * Editor for compensation preferences (salary range, equity, benefits)
 * ⚠️ FIXED: Uses correct field names matching backend schema
 */
import React from 'react';
import {
  IonItem,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonList,
  IonChip,
  IonIcon,
  IonText,
  IonButton,
  IonToggle,
} from '@ionic/react';
import { closeCircle } from 'ionicons/icons';
import { CompensationPreferences } from '../../types/preferences.ts';
import './PreferenceEditor.css';

interface CompensationPreferenceEditorProps {
  data: CompensationPreferences;
  onChange: (data: CompensationPreferences) => void;
}

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR'];

const CompensationPreferenceEditor: React.FC<CompensationPreferenceEditorProps> = ({
  data,
  onChange,
}) => {
  const [newBenefit, setNewBenefit] = React.useState('');

  const handleFieldChange = (field: keyof CompensationPreferences, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleAddBenefit = () => {
    if (newBenefit.trim()) {
      const currentList = (data.benefits_priorities || []) as string[];
      onChange({
        ...data,
        benefits_priorities: [...currentList, newBenefit.trim()],
      });
      setNewBenefit('');
    }
  };

  const handleRemoveBenefit = (index: number) => {
    const currentList = (data.benefits_priorities || []) as string[];
    onChange({
      ...data,
      benefits_priorities: currentList.filter((_, i) => i !== index),
    });
  };

  return (
    <IonList className="preference-editor-list">
      <IonItem>
        <IonLabel position="stacked">Currency</IonLabel>
        <IonSelect
          value={data.currency || 'USD'}
          onIonChange={(e) => handleFieldChange('currency', e.detail.value)}
        >
          {CURRENCIES.map((currency) => (
            <IonSelectOption key={currency} value={currency}>
              {currency}
            </IonSelectOption>
          ))}
        </IonSelect>
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">
          <h3>Minimum Salary</h3>
          <p>Annual base salary (minimum)</p>
        </IonLabel>
        <IonInput
          type="number"
          value={data.min || ''}
          placeholder="e.g., 80000"
          onIonChange={(e) => handleFieldChange('min', e.detail.value ? parseInt(e.detail.value) : undefined)}
        />
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">
          <h3>Maximum Salary</h3>
          <p>Annual base salary (maximum)</p>
        </IonLabel>
        <IonInput
          type="number"
          value={data.max || ''}
          placeholder="e.g., 150000"
          onIonChange={(e) => handleFieldChange('max', e.detail.value ? parseInt(e.detail.value) : undefined)}
        />
      </IonItem>

      {data.min && data.max && (
        <IonItem lines="none">
          <IonLabel>
            <IonText color="medium">
              <p style={{ textAlign: 'center', fontSize: '0.9rem' }}>
                Target range: {data.currency || 'USD'} {data.min.toLocaleString()} - {data.max.toLocaleString()} / year
              </p>
            </IonText>
          </IonLabel>
        </IonItem>
      )}

      <IonItem>
        <IonLabel>
          <h3>Equity Important</h3>
          <p>Is equity a key factor in your decision?</p>
        </IonLabel>
        <IonToggle
          checked={data.equity_important || false}
          onIonChange={(e) => handleFieldChange('equity_important', e.detail.checked)}
        />
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">Important Benefits</IonLabel>
        <div className="chip-input-container">
          {data.benefits_priorities && data.benefits_priorities.length > 0 && (
            <div className="chip-list">
              {data.benefits_priorities.map((benefit, index) => (
                <IonChip key={index} color="success">
                  {benefit}
                  <IonIcon icon={closeCircle} onClick={() => handleRemoveBenefit(index)} />
                </IonChip>
              ))}
            </div>
          )}
          <div className="chip-input">
            <IonInput
              value={newBenefit}
              placeholder="e.g., Health insurance, 401k"
              onIonChange={(e) => setNewBenefit(e.detail.value || '')}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddBenefit();
                }
              }}
            />
            <IonButton onClick={handleAddBenefit} size="small">
              Add
            </IonButton>
          </div>
        </div>
      </IonItem>
    </IonList>
  );
};

export default CompensationPreferenceEditor;
